/**
 * Response types for the rw26 v2 widget service's neighborhood endpoint
 * (`POST /widget/v2/neighborhood`).
 *
 * Hand-mirrored from the service's TypeBox contract
 * (rw26 `stacks/content/widgets-web/contracts/src/v2/neighborhood.ts` and
 * `.../v2/shared.ts`). The contract package is not publishable into this
 * monorepo, so duplication is structural rather than accidental — the same
 * tradeoff `client/plugin/src/services/v2DataUtils.ts` already makes. Keep the
 * optionality here matching the contract exactly: a field typed required that the
 * service omits is how an adapter silently produces `undefined` downstream.
 */

export type V2LatLng = {
    lat: number;
    lng: number;
};

export type V2Viewport = {
    northeast: V2LatLng;
    southwest: V2LatLng;
};

export type V2PlaceInfo = {
    /** Flattened location/viewport — what gen2 widgets read. Always present. */
    location: V2LatLng;
    viewport: V2Viewport;
    /**
     * The same values under the legacy Google-Places nesting, served
     * specifically for this widget. Optional in the contract, so every consumer
     * must be able to fall back to the flattened fields above — this widget's
     * map centering, clustering, and bounds fitting all read `geometry`.
     */
    geometry?: {
        location: V2LatLng;
        viewport: V2Viewport;
    };
    place_id: string;
    types?: string[];
    plus_code?: {
        compound_code: string;
        global_code: string;
    };
    formatted_address?: string;
};

export type V2TopJobMedia = {
    id: string;
    type: string;
    url: string;
    thumbnailUrl: string;
    caption: string;
};

export type V2TopJob = {
    id: string;
    /** ISO date string. Absent for jobs with no start/end date. */
    completedAt?: string;
    /**
     * ISO date string of the job's start, distinct from `completedAt`. The
     * pop-out header renders a start–end range, so it needs the start on its
     * own. Added to the service's TopJob shape by EN-1328; absent from
     * responses cached before that shipped, and for dateless jobs.
     */
    startedAt?: string;
    published: boolean;
    /** Absent for jobs that never resolved to a coordinate. */
    geolocation?: V2LatLng;
    /**
     * City/state/zip of the worksite. Added to TopJob by EN-1328 to feed the
     * pop-out header's location line; absent from pre-EN-1328 cached responses,
     * and any field can be missing for a partially-located job.
     */
    locationInfo?: {
        city?: string;
        state?: string;
        zip?: string;
    };
    coverImageUrl: string;
    title?: string;
    reviewId?: string;
    /**
     * Source and rating of the job's linked review, for the job-card review
     * badge. Not served by v2 yet — the stored job carries only `reviewId` (see
     * the EN-1328 deferred re-bake / serve-time join). Typed here so
     * `adaptV2FeaturedJob` maps them through on arrival, no further app change.
     */
    reviewSource?: string;
    reviewRating?: number;
    description?: string;
    additionalMediaCount: number;
    additionalMedia: V2TopJobMedia[];
};

/**
 * An anonymized map-density pin: a non-published worksite reduced to id +
 * coordinate. The map renders these as unlabeled `UnpublishedMarker` dots whose
 * popover reads only "Completed project", so no title/media/description is
 * exposed. `geolocation` is optional — a worksite can lack a coordinate — and
 * `NeighborhoodMarkers` skips pins without one.
 */
export type V2DensityJob = {
    id: string;
    geolocation?: V2LatLng;
};

export type V2TopReview = {
    id: string;
    source?: string;
    /** ISO date string. */
    postedAt?: string;
    authorName?: string;
    authorPhotoUrl?: string;
    rating?: string;
    ratingNum?: number;
    updatedAt?: string;
    message?: string;
    externalURL?: string;
    /**
     * The business owner's reply to the review, from `Content.thread[0]`. Not
     * served by v2 yet — the service-area aggregate does not store it (see the
     * EN-1328 deferred re-bake). Typed here so `adaptV2FeaturedReview` maps it
     * through the moment the re-bake lands, with no further app change. `postedAt`
     * is an ISO date string.
     */
    response?: {
        author?: string;
        message?: string;
        postedAt?: string;
    };
    /**
     * The worksite this review is attached to, for the review→job link. Not
     * served by v2 yet (same deferred re-bake); mapped through on arrival.
     */
    jobId?: string;
};

export type V2NeighborhoodServiceArea = {
    id: string;
    /** The postcode; `ServiceArea.name` on the legacy model. */
    name: string;
    /** `displayName || name` — already resolved by the service. */
    neighborhood: string;
    postcode: string;
    placeInfo?: V2PlaceInfo;
    /** Capped at EMBEDDED_TOP_JOBS_LIMIT (10) by the service. */
    jobs: V2TopJob[];
    /**
     * Anonymized density pins — non-published worksites the service embeds whole
     * (never paged) alongside the published `jobs`. Required in the rw26
     * contract, but typed optional here: a response cached before the density
     * work (RFC-NEIGHBORHOOD-MAP-DENSITY) shipped omits it, and the adapter
     * defaults it to `[]` rather than trusting it present. Empty for areas not
     * yet re-baked.
     */
    densityJobs?: V2DensityJob[];
    /** Capped at EMBEDDED_TOP_REVIEWS_LIMIT (5) by the service. */
    reviews: V2TopReview[];
    /** Published-only count. Retained alongside `jobCountTotal`. */
    jobCount: number;
    /**
     * All-status count (published + non-published) — what the legacy map
     * displayed and what clustering/centering weight by. Optional here for the
     * same reason as `densityJobs`; the adapter falls back to the published-only
     * `jobCount` when a response predates the density work.
     */
    jobCountTotal?: number;
    reviewCount: number;
    averageRating: number;
    hasMoreJobs: boolean;
    hasMoreReviews: boolean;
    hasReviews: boolean;
    hasJobs: boolean;
    summary?: string;
};

export type V2NeighborhoodResponse = {
    serviceAreas: V2NeighborhoodServiceArea[];
    /** Only set when a postcode was supplied or resolved from the client IP. */
    resolvedPostcode?: string;
    hasMore: boolean;
};

/**
 * Request body for `POST /widget/v2/neighborhood`.
 *
 * `postcode` given filters to that one area; omitted returns every area the
 * account serves. The response is a pure function of this body — the service
 * derives nothing from headers.
 */
export type V2NeighborhoodRequest = {
    accountId: string;
    page: number;
    limit: number;
    postcode?: string;
};

/** The service rejects `limit` above this (contract `maximum`). */
export const V2_NEIGHBORHOOD_MAX_LIMIT = 100;

export type V2ServiceAreaContentType = 'jobs' | 'reviews';

type V2ServiceAreaContentRequestBase = {
    accountId: string;
    /** The service area's postcode (`ServiceArea.name` on the legacy model). */
    postcode: string;
    page: number;
    limit: number;
};

/**
 * Request body for `POST /widget/v2/serviceArea/content` — pages one area's jobs
 * or reviews beyond the window embedded in the neighborhood response.
 *
 * Discriminated on `type` so a caller cannot ask for reviews and read the answer
 * as jobs: the route returns a different element shape per `type`, and the same
 * rule the header states about optionality applies here one level up. Only the
 * 'jobs' arm is used today; the reviews path is a later phase.
 */
export type V2ServiceAreaContentRequest<
    T extends V2ServiceAreaContentType = V2ServiceAreaContentType
> = V2ServiceAreaContentRequestBase & { type: T };

type V2ServiceAreaContentData = {
    jobs: V2TopJob[];
    reviews: V2TopReview[];
};

export type V2ServiceAreaContentResponse<
    T extends V2ServiceAreaContentType = V2ServiceAreaContentType
> = {
    /** Element shape follows the request's `type` — see the request above. */
    data: V2ServiceAreaContentData[T];
    page: number;
    hasMore: boolean;
    /** The area's full `jobCount`/`reviewCount`, not the stored-array length. */
    totalCount: number;
};

/**
 * Jobs embedded directly in a neighborhood response (rw26
 * `EMBEDDED_TOP_JOBS_LIMIT`). Paging `serviceArea/content` at exactly this limit
 * makes page 1 identical to the embedded window, so a client that already has the
 * embedded jobs can continue from page 2 with no duplicate or skipped entry. Any
 * other limit misaligns the page boundaries.
 */
export const V2_EMBEDDED_JOBS_LIMIT = 10;

/**
 * Reviews embedded directly in a neighborhood response (rw26
 * `EMBEDDED_TOP_REVIEWS_LIMIT`). The reviews analogue of
 * {@link V2_EMBEDDED_JOBS_LIMIT}: paging `serviceArea/content` for reviews at
 * exactly this limit makes page 1 identical to the embedded window, so a client
 * that already rendered the embedded reviews continues from page 2 with no
 * duplicate or skipped entry.
 */
export const V2_EMBEDDED_REVIEWS_LIMIT = 5;

/**
 * How deep `serviceArea/content` can page (rw26 `STORED_JOBS_LIMIT`). The
 * aggregate stores this many published jobs per area, and `hasMore` goes false
 * here even when `totalCount` is larger.
 *
 * Raised 50 → 250 with RFC-NEIGHBORHOOD-MAP-DENSITY: the service now stores up to
 * 250 published jobs per area, so the paging loop must reach that depth or the
 * map loses published pins above the old cap. Non-published pins arrive
 * separately as `densityJobs`, embedded whole. The residual parity gap is only
 * areas with more than 250 *published* jobs, which are rare — the p99 area is far
 * below it.
 */
export const V2_STORED_JOBS_LIMIT = 250;

/**
 * How deep `serviceArea/content` can page for reviews (rw26
 * `STORED_REVIEWS_LIMIT`). The aggregate stores this many reviews per area;
 * past it `hasMore` is false even when `totalCount` is larger. Bounds the
 * by-id review walk so it cannot page forever against a service that kept
 * reporting `hasMore`.
 */
export const V2_STORED_REVIEWS_LIMIT = 50;
